import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, resolveImage, API_URL } from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Upload, X, FileText } from "lucide-react";

const inputCls =
  "w-full bg-transparent border-b border-[#E5DFD5] py-2.5 text-sm focus:outline-none focus:border-[#C5A880]";

export default function AdminSettings() {
  const [page, setPage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/pages/settings");
        setPage(data);
      } catch (_err) {
        setPage({ key: "settings", title: "Site Settings", brochure_pdf: "" });
      }
    })();
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please choose a PDF file");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPage({ ...page, brochure_pdf: data.url });
      toast.success("Brochure uploaded — remember to Save");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { key: _, ...payload } = page;
      await api.put("/admin/pages/settings", payload);
      toast.success("Settings saved");
    } catch (_err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!page)
    return (
      <div className="p-10">
        <Loader2 className="w-6 h-6 animate-spin text-[#C5A880]" strokeWidth={1.5} />
      </div>
    );

  const brochureUrl = page.brochure_pdf ? `${API_URL}${page.brochure_pdf}` : "";

  return (
    <div className="p-10 lg:p-16 max-w-[900px]" data-testid="admin-settings">
      <Link to="/admin/pages" className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#73706B] hover:text-[#1A1A1A] mb-8">
        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> All Pages
      </Link>
      <h1 className="text-4xl lg:text-5xl font-light mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        Settings & Brochure
      </h1>
      <p className="text-sm text-[#73706B] mb-12">
        Upload the master PDF brochure that is automatically emailed to every new lead.
      </p>

      <form onSubmit={save} className="space-y-10">
        <section className="space-y-4">
          <h2 className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">— Master brochure PDF</h2>

          <div
            className="border border-[#E5DFD5] bg-[#F4EFE4] px-5 py-4 flex items-start gap-3 text-sm text-[#5C5950]"
            data-testid="media-deploy-safe-banner"
          >
            <span className="inline-block w-2 h-2 rounded-full bg-[#7BA890] mt-1.5" />
            <p>
              <strong className="font-medium text-[#1A1A1A]">Media auto-saves in the database</strong> — all uploaded brochures and images are stored in MongoDB and survive every redeployment. No need to re-upload after going live.
            </p>
          </div>

          {page.brochure_pdf ? (
            <div className="border border-[#E5DFD5] bg-[#FFFFFF] p-6 flex items-center gap-5">
              <FileText className="w-8 h-8 text-[#C5A880]" strokeWidth={1.25} />
              <div className="flex-1">
                <p className="text-base text-[#1A1A1A]">Brochure attached</p>
                <a href={brochureUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-[#73706B] hover:text-[#C5A880] break-all">
                  {brochureUrl}
                </a>
              </div>
              <button
                type="button"
                onClick={() => setPage({ ...page, brochure_pdf: "" })}
                data-testid="brochure-clear"
                className="text-[#73706B] hover:text-red-700"
              >
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>
          ) : (
            <div className="border border-dashed border-[#E5DFD5] bg-[#FFFFFF] p-10 text-center">
              <FileText className="w-8 h-8 text-[#C5A880] mx-auto mb-3" strokeWidth={1.25} />
              <p className="text-sm text-[#73706B] mb-4">No brochure uploaded yet.</p>
            </div>
          )}

          <input
            ref={fileRef}
            type="file"
            accept="application/pdf"
            onChange={handleUpload}
            className="hidden"
            data-testid="brochure-file-input"
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="brochure-upload-btn"
            className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#F9F6F0] px-6 py-3 text-[10px] tracking-[0.25em] uppercase hover:bg-[#C5A880] disabled:opacity-60 transition-colors"
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} /> : <Upload className="w-3.5 h-3.5" strokeWidth={1.5} />}
            {uploading ? "Uploading" : page.brochure_pdf ? "Replace brochure" : "Upload brochure (PDF)"}
          </button>
        </section>

        <div className="flex items-center gap-4 border-t border-[#E5DFD5] pt-8">
          <button type="submit" disabled={saving} data-testid="settings-save" className="bg-[#1A1A1A] text-[#F9F6F0] px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-[#C5A880] disabled:opacity-60 transition-colors inline-flex items-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />}
            {saving ? "Saving" : "Save Settings"}
          </button>
        </div>
      </form>
    </div>
  );
}
